// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";

// IMPORT UI COMPONENTS
import {
  Box,
  Checkbox,
  Grid,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PAGE_SIZE } from "../../services/common/const";
import dayjs from "dayjs";
import { PreferencesContext } from "../../PreferenceContext";
import {
  GetNotificationList,
  UpdateNotificationLog,
} from "../../services/NotificationService";
import SearchIcon from "@mui/icons-material/Search";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import { EncryptDecryptService } from "../../services/common/EncryptionService";

// SCHEMA
interface Data {
  notificationId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  notificationId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  notificationId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

type Order = "desc" | "asc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

// SCHEMA FOR RESULTDATA
interface ResultData {
  notificationColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  notificationColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

const ActivityList = () => {
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  const [value, setValue] = useState("");
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [order, setOrder] = React.useState<Order>("");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [page_Index, setPage_Index] = useState(1);
  const [counData, setCounData] = useState(0);
  const [Curr_page, setCurrPage] = React.useState(1);
  const smallScreen = useMediaQuery("(max-width:768px)");

  const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  };

  const queryParams = useQuery();
  const encodedQuery = queryParams.get("q") || "";
  const pageFromUrl = parseInt(queryParams.get("page") || "1", 10);
  const decodedQuery = decodeURIComponent(encodedQuery);

  const [param, setParam] = useState({
    ...parameters,
    pageIndex: pageFromUrl,
    searchValue: decodedQuery,
    startDate: queryParams.get("fd") || "",
    endDate: queryParams.get("ed") || "",
  });

  const [searchValue, setSearchValue] = useState(param.searchValue);

  const debouncedSearch = useMemo(() => {
    let handler: NodeJS.Timeout;
    return (value: string) => {
      clearTimeout(handler);
      handler = setTimeout(() => {
        const val = encodeURIComponent(value.trim());

        // 🔧 Fix: Update param state before navigation
        setParam((prev) => ({
          ...prev,
          searchValue: decodeURIComponent(val),
          pageIndex: 1,
        }));

        navigate(`?page=1&q=${val}&fd=${param.startDate}&ed=${param.endDate}`);
      }, 400);
    };
  }, [navigate, param.startDate, param.endDate]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const selectedNotification = useMemo(() => {
    if (selected.length === 0) return null;

    return resultData.notificationColl.find(
      (n) => n.notificationUserMappingId === selected[0]
    );
  }, [selected, resultData]);

  const toggleReadUnread = async (isRead: boolean) => {
    try {
      // Create JSON for ALL selected items
      const payload = selected.map((id) => {
        const item = resultData.notificationColl.find(
          (n) => n.notificationId === id
        );
        return {
          notificationUserMappingId: item.notificationUserMappingId,
          isRead,
        };
      });

      await UpdateNotificationLog({
        notificationDetailjson: JSON.stringify(payload),
        dataManagedBy: userContext.loginDetail.userId,
      });

      // Update UI for ALL selected
      setResultData((prev) => ({
        ...prev,
        notificationColl: prev.notificationColl.map((row) =>
          selected.includes(row.notificationId) ? { ...row, isRead } : row
        ),
      }));
    } catch (err) {
      console.error("Notification read update failed", err);
    }
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell align="left">
            <Checkbox
              indeterminate={
                selected.length > 0 &&
                selected.length < resultData.notificationColl.length
              }
              checked={isAllSelected}
              onChange={handleCheckSelectAll}
            />
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "35%",
            }}
            align="left"
          >
            Description
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                sm: "table-cell",
              },
              fontWeight: "bold",
              width: "25%",
            }}
            align="center"
          >
            Form
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                sm: "table-cell",
              },
              fontWeight: "bold",
              width: "30%",
            }}
            align="left"
          >
            Created By
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                sm: "table-cell",
              },
              fontWeight: "bold",
              width: "30%",
            }}
            align="left"
          >
            Created Date
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  const isAllSelected =
    resultData.notificationColl.length > 0 &&
    selected.length === resultData.notificationColl.length;

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.notificationColl?.map(
        (n: any) => n?.notificationId || 0
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (Curr_page !== value) {
      setCurrPage(value);
      setPage_Index(value);
      setParam({ ...param, pageIndex: value });
    }
  };

  const getReferBookActivityList = async () => {
    try {
      let params = new URLSearchParams();

      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetNotificationList(params);
      if (result?.data?.data?.notificationColl) {
        const resultData: ResultData = result?.data?.data;
        let notificationColl = resultData?.notificationColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (page_Index - 1) * param.pageSize + 1;
        let endIndex =
          (page_Index - 1) * param.pageSize + (notificationColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  const fetchData = async () => {
    try {
      await getReferBookActivityList();
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
    setIsLoading(false);
  }, [param]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className="main-container">
        <Grid container className="page-header">
          <Grid item xs={6}>
            <Box sx={{ display: "flex", height: "100%", alignItems: "end" }}>
              <CustomBreadcrumbs />
            </Box>
          </Grid>

          <Grid container className="list-container" mt={2}>
            <Grid item xs={12}>
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} sm={6} md={3} xl={2}>
                  {/* Search TextField */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: smallScreen ? 300 : 220,
                      width: "100%",
                    }}
                  >
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-input"
                    >
                      <TextField
                        id="searchBox"
                        placeholder="Search..."
                        value={searchValue}
                        defaultValue={decodedQuery}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                        sx={{ flexShrink: 0 }}
                        InputProps={{
                          startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                        }}
                      />
                    </SkeletonWrapper>
                    {/* TOGGLE READ/UNREAD ICON */}
                    {selected.length > 0 && (
                      <Box
                        sx={{
                          ml: 2,
                          display: "flex",
                          gap: 1,
                          cursor: "pointer",
                          fontSize: 26,
                        }}
                      >
                        {selectedNotification?.isRead ? (
                          <Tooltip title="Mark as Unread">
                            <MarkEmailUnreadOutlinedIcon
                              onClick={() => toggleReadUnread(false)}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Mark as Read">
                            <DraftsOutlinedIcon
                              onClick={() => toggleReadUnread(true)}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            {!smallScreen && (
              <Grid item xs={12} mt={2}>
                <TableContainer>
                  <Table>
                    <EnhancedTableHead
                      numSelected={resultData?.notificationColl?.length || 0}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleCheckSelectAll}
                      rowCount={resultData?.notificationColl?.length}
                    />
                    <TableBody>
                      {isLoading ? (
                        Array(skeletonRowCount)
                          ?.fill(0)
                          ?.map((skeletonRow, index) => {
                            return (
                              <TableRow
                                key={"skeleton-row" + index?.toString()}
                              >
                                <TableCell colSpan={8}>
                                  <SkeletonWrapper
                                    isLoading={isLoading}
                                    className="skeleton-input"
                                  >
                                    <span></span>
                                  </SkeletonWrapper>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <>
                          {stableSort(
                            resultData?.notificationColl,
                            getComparator(order, orderBy)
                          )
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((item: any, index: any) => {
                              return (
                                <TableRow
                                  key={`referBookEntry-row-${item?.referBookEntryId || index
                                    }`}
                                >
                                  <TableCell align="left">
                                    <Checkbox
                                      checked={selected.includes(
                                        item.notificationId
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelected([
                                            ...selected,
                                            item.notificationId,
                                          ]);
                                        } else {
                                          setSelected(
                                            selected.filter(
                                              (id) => id !== item.notificationId
                                            )
                                          );
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      display: {
                                        xs: "none",
                                        sm: "table-cell",
                                      },
                                      fontWeight: item?.isRead
                                        ? "normal"
                                        : "bold",
                                    }}
                                  >
                                    {item?.tableName === "ReferBookEntry" ? (
                                      <Link
                                        key={`link-${item?.referBookEntryId}`}
                                        className={
                                          item?.isRead
                                            ? "text-link-normal"
                                            : "text-link"
                                        }
                                        to={{
                                          pathname:
                                            "/master/refer-book-entry-list/refer-book-entry-details",
                                          search: `?q=${encodeURIComponent(
                                            edService.encrypt(
                                              item?.referBookEntryId || 0
                                            ) || 0
                                          )}`,
                                        }}
                                      >
                                        {item?.notificationData}
                                      </Link>
                                    ) : (
                                      item?.notificationData
                                    )}
                                  </TableCell>

                                  <TableCell
                                    sx={{
                                      display: {
                                        xs: "none",
                                        sm: "table-cell",
                                      },
                                    }}
                                    align="center"
                                  >
                                    {item?.tableName}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      display: {
                                        xs: "none",
                                        sm: "table-cell",
                                      },
                                    }}
                                  >
                                    {item?.fromUserFullName}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      display: {
                                        xs: "none",
                                        sm: "table-cell",
                                      },
                                    }}
                                  >
                                    {item?.createdDate
                                      ? dayjs(item.createdDate).format(
                                        "DD/MM/YYYY hh:mm A"
                                      )
                                      : ""}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          {resultData?.notificationColl?.length <= 0 && (
                            <TableRow>
                              <TableCell align="center" colSpan={8}>
                                No record(s) found
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
            {smallScreen && (
              <Grid item xs={12} sx={{ paddingTop: "8px" }}>
                {resultData?.notificationColl?.map((item, index) => (
                  <Box
                    key={"mobile_card_" + index}
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: 2,
                      padding: 2,
                      marginBottom: 2,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography variant="body2">
                      Description: <strong>{item.notificationData}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Form: <strong> {item.tableName}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Created By: <strong>{item.fromUserFullName}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Created Date:{" "}
                      <strong>
                        {item?.createdDate
                          ? dayjs(item.createdDate).format("DD/MM/YYYY hh:mm A")
                          : ""}
                      </strong>
                    </Typography>
                  </Box>
                ))}
              </Grid>
            )}
            <Grid item xs={12} paddingTop={1}>
              {resultData?.notificationColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={Curr_page} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ActivityList;
